import { useQueryClient } from '@tanstack/react-query'
import { Divider, type RefSelectProps } from 'antd'
import { useAtomValue } from 'jotai'
import {
  forwardRef,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
  type Ref
} from 'react'
import { FaPlus } from 'react-icons/fa'

import { Button, Select } from '@/components/elements'
import { type SingleOrMultipleSelectProps } from '@/components/types'
import { ACTIONS, STATUSES } from '@/constants/common/message.constants'
import { type TruckModelEntity } from '@/libs/api'
import { useSelectOnChangeHandler } from '@/libs/hooks'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'

import { useTruckModelsSelectQuery } from '../../hooks/use-truck-models-select-query'
import { truckModelsQueryKeys } from '../../query-keys'
import { TruckModelCreateModal } from '../TruckModelCreateModal'

import { mapToTruckModelFieldName } from './map-to-truck-model-field-name'

type Props = SingleOrMultipleSelectProps<number, TruckModelEntity> & {
  tenantKey: string
  /** 拠点ID指定（指定しない場合は選択された拠点IDを使用して絞り込まれる） */
  dcId?: number
  onCreate?: () => void
  hideCreateButton?: boolean
  shippingDate?: string
  /** 選択肢一覧の末尾に追加表示する要素（例: 操作ヒント） */
  dropdownFooter?: ReactNode
}

/**
 * 車種を選択するSelect
 */
export const TruckModelSelect = forwardRef(
  (
    {
      placeholder = STATUSES.UNSELECTED,
      mode,
      onChange,
      onCreate,
      tenantKey,
      dcId,
      value,
      hideCreateButton,
      shippingDate,
      dropdownFooter,
      ...props
    }: Props,
    ref: Ref<RefSelectProps> | undefined
  ) => {
    const [isOpenCreateModal, setIsOpenCreateModal] = useState(false)
    const selectedDcId = useAtomValue(selectedDcIdAtom)

    const queryClient = useQueryClient()

    const handleChange = useSelectOnChangeHandler({ mode, onChange })

    const handleCreateSuccess = useCallback(
      async (truckModel: TruckModelEntity) => {
        await queryClient.invalidateQueries({
          queryKey: truckModelsQueryKeys.select(tenantKey, dcId ?? selectedDcId)
        })
        handleChange(truckModel.id, truckModel)
        setIsOpenCreateModal(false)

        if (onCreate) onCreate()
      },
      [queryClient, tenantKey, handleChange, onCreate, dcId, selectedDcId]
    )

    const { data, isFetching, isLoading } = useTruckModelsSelectQuery(
      tenantKey,
      { dcId: dcId ?? selectedDcId, shippingDate }
    )

    const options = useMemo(() => {
      return data
        ?.sort((a, b) => a.code.localeCompare(b.code))
        ?.map((truckModel) => ({
          ...truckModel,
          // nameで指定すると利用側で取得できるoption.nameの値が変わるため、labelで指定
          label: mapToTruckModelFieldName(truckModel)
        }))
    }, [data])

    return (
      <>
        <Select
          customRef={ref}
          mode={mode}
          value={isLoading ? null : value}
          placeholder={placeholder}
          options={options}
          loading={isFetching}
          onChange={handleChange}
          {...props}
          dropdownRender={(menu) => (
            <div>
              {menu}
              {!hideCreateButton && (
                <>
                  <Divider className="mx-0 my-2" />
                  <div className="text-center">
                    <Button
                      icon={<FaPlus />}
                      onClick={() => setIsOpenCreateModal(true)}
                    >
                      {ACTIONS.CREATE_TRUCK_MODEL}
                    </Button>
                  </div>
                </>
              )}
              {dropdownFooter}
            </div>
          )}
        />
        <TruckModelCreateModal
          open={isOpenCreateModal}
          tenantKey={tenantKey}
          onSuccess={handleCreateSuccess}
          onClose={() => setIsOpenCreateModal(false)}
          dcId={dcId}
        />
      </>
    )
  }
)
